import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import store from '../../store/index';
import SpinnerLoader from '../../components/common/SpinnerLoader';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DataTable from 'react-data-table-component';
import DataTableExtensions from "react-data-table-component-extensions";
import 'react-data-table-component-extensions/dist/index.css';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaEye, 
  FaPaperPlane, 
  FaSave,
  FaTimes,
  FaEnvelope,
  FaGavel,
  FaCopy,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';

// Email Template Management Component
const EmailTemplateManagement = ({ permissions, authInfoString }) => {
  const history = useHistory();
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  // Available email template variables
  const availableVariables = [
    'username', 'firstName', 'lastName', 'email', 'siteName', 'siteUrl',
    'orderNumber', 'orderDate', 'orderTotal', 'projectName', 'projectId',
    'milestoneTitle', 'milestoneDescription', 'amount', 'paymentDate',
    'invoiceNumber', 'dueDate', 'supportUrl', 'loginUrl', 'dashboardUrl'
  ];

  // Validation Schema
  const emailTemplateValidationSchema = Yup.object({
    name: Yup.string()
      .required('Template name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name cannot exceed 100 characters'),
    tag: Yup.string()
      .required('Tag is required')
      .matches(/^[a-z_]+$/, 'Tag must contain only lowercase letters and underscores'),
    subject: Yup.string()
      .required('Subject is required')
      .min(5, 'Subject must be at least 5 characters')
      .max(200, 'Subject cannot exceed 200 characters'),
    body: Yup.string()
      .required('Email body is required')
      .min(10, 'Body must be at least 10 characters'),
    category: Yup.string()
      .required('Category is required')
  });

  // Load Email Templates
  const loadEmailTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get('admin/email-templates', {
        headers: {
          Authorization: `Bearer ${authInfoString.token}`,
        },
      });
      setEmailTemplates(res.data.data || sampleEmailTemplates);
    } catch (error) {
      console.error("Error loading email templates: ", error);
      // Use sample data for demo
      setEmailTemplates(sampleEmailTemplates);
    } finally {
      setLoading(false);
    }
  };

  // Sample data for demo
  const sampleEmailTemplates = [
    {
      id: 1,
      name: 'Welcome Email',
      tag: 'welcome',
      category: 'user_registration',
      subject: 'Welcome to {{siteName}}, {{firstName}}!',
      body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333;">Welcome {{firstName}}!</h1>
        <p>Thank you for joining {{siteName}}. We're excited to have you on board.</p>
        <p>You can access your dashboard at: <a href="{{dashboardUrl}}">{{dashboardUrl}}</a></p>
        <p>If you have any questions, feel free to contact our support team.</p>
        <p>Best regards,<br>The {{siteName}} Team</p>
      </div>`,
      active: true,
      created_at: '2024-01-15'
    },
    {
      id: 2,
      name: 'Order Confirmation',
      tag: 'order_confirmation',
      category: 'orders',
      subject: 'Order Confirmation #{{orderNumber}}',
      body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #28a745;">Order Confirmed!</h1>
        <p>Hi {{firstName}},</p>
        <p>Your order #{{orderNumber}} has been confirmed and is being processed.</p>
        <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <h3>Order Details:</h3>
          <p><strong>Order Number:</strong> {{orderNumber}}</p>
          <p><strong>Order Date:</strong> {{orderDate}}</p>
          <p><strong>Total Amount:</strong> {{orderTotal}}</p>
        </div>
        <p>Thank you for your business!</p>
      </div>`,
      active: true,
      created_at: '2024-01-10'
    },
    {
      id: 3,
      name: 'Milestone Approved',
      tag: 'milestone_approved',
      category: 'projects',
      subject: 'Milestone Approved for {{projectName}}',
      body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #007bff;">Great News!</h1>
        <p>Hi {{firstName}},</p>
        <p>Your milestone "<strong>{{milestoneTitle}}</strong>" for project "{{projectName}}" has been approved.</p>
        <p>{{milestoneDescription}}</p>
        <p>You can view the details in your dashboard: <a href="{{dashboardUrl}}">{{dashboardUrl}}</a></p>
        <p>Congratulations on your progress!</p>
      </div>`,
      active: true,
      created_at: '2024-01-08'
    },
    {
      id: 4,
      name: 'Payment Released',
      tag: 'payment_released',
      category: 'payments',
      subject: 'Payment Released - {{amount}}',
      body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #28a745;">Payment Released!</h1>
        <p>Hi {{firstName}},</p>
        <p>Your payment of <strong>{{amount}}</strong> for project "{{projectName}}" has been released.</p>
        <div style="background: #d4edda; border: 1px solid #c3e6cb; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <p><strong>Payment Date:</strong> {{paymentDate}}</p>
          <p><strong>Project:</strong> {{projectName}}</p>
          <p><strong>Amount:</strong> {{amount}}</p>
        </div>
        <p>The payment should appear in your account within 1-3 business days.</p>
      </div>`,
      active: true,
      created_at: '2024-01-05'
    }
  ];

  useEffect(() => {
    loadEmailTemplates();
  }, []);

  // Create/Update Email Template
    const handleSaveTemplate = async (values, { setSubmitting, resetForm }) => {
        try {
            // Add createdBy/updatedBy fields
            const dataToSend = {
            ...values,
            createdBy: editingTemplate ? undefined : authInfoString.id,
            updatedBy: editingTemplate ? authInfoString.id : undefined
            };

            const endpoint = editingTemplate 
            ? `admin/email-templates/update/${editingTemplate.id}`
            : 'admin/email-templates/create';
            
            const method = editingTemplate ? 'put' : 'post';
            
            const res = await axios[method](endpoint, dataToSend, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            if (res.data.status) {
            toast.success(`Template ${editingTemplate ? 'updated' : 'created'} successfully!`);
            setShowCreateModal(false);
            setEditingTemplate(null);
            resetForm();
            loadEmailTemplates();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error saving template');
        } finally {
            setSubmitting(false);
        }
    };

  // Delete Template
    const handleDeleteTemplate = async () => {
        if (!selectedTemplate) return;

        try {
            await axios.delete(`admin/email-templates/delete/${selectedTemplate.id}`, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            toast.success('Template deleted successfully!');
            setShowDeleteModal(false);
            setSelectedTemplate(null);
            loadEmailTemplates();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error deleting template');
        }
    };

  // Send Test Email
    const handleSendTestEmail = async (template) => {
        try {
            await axios.post(`admin/email-templates/test/${template.id}`, {
            email: authInfoString.email
            }, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            toast.success('Test email sent successfully!');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error sending test email');
        }
    };

  // Toggle Template Status
    const handleToggleStatus = async (template) => {
        try {
            await axios.patch(`admin/email-templates/toggle/${template.id}`, {
            updatedBy: authInfoString.id
            }, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            toast.success('Template status updated!');
            loadEmailTemplates();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating status');
        }
    };

  // Email Template Columns
  const emailTemplateColumns = [
    {
      name: 'TEMPLATE NAME',
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.name}</div>
          <small className="text-muted">{row.category}</small>
        </div>
      ),
    },
    {
      name: 'TAG',
      selector: (row) => row.tag,
      sortable: true,
      cell: (row) => <span className="badge bg-secondary font-monospace">{row.tag}</span>,
    },
    {
      name: 'SUBJECT',
      selector: (row) => row.subject,
      sortable: true,
      cell: (row) => (
        <div className="text-truncate" style={{maxWidth: '250px'}} title={row.subject}>
          {row.subject}
        </div>
      ),
    },
    {
      name: 'STATUS',
      selector: (row) => row.active,
      sortable: true,
      cell: (row) => (
        <button
          onClick={() => handleToggleStatus(row)}
          className="btn btn-sm p-0 border-0 d-flex align-items-center"
          disabled={!permissions.edit}
        >
          {row.active ? (
            <FaToggleOn className="text-success me-1" size={20} />
          ) : (
            <FaToggleOff className="text-muted me-1" size={20} />
          )}
          <span className={row.active ? 'text-success' : 'text-muted'}>
            {row.active ? 'Active' : 'Inactive'}
          </span>
        </button>
      ),
    },
    {
      name: 'ACTIONS',
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => {
              setSelectedTemplate(row);
              setShowPreviewModal(true);
            }}
            title="Preview"
          >
            <FaEye />
          </button>
          <button
            className="btn btn-sm btn-outline-success"
            onClick={() => handleSendTestEmail(row)}
            title="Send Test"
          >
            <FaPaperPlane />
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            disabled={!permissions.edit}
            onClick={() => {
              setEditingTemplate(row);
              setShowCreateModal(true);
            }}
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={() => {
              const duplicated = {...row, id: null, name: `${row.name} (Copy)`, tag: `${row.tag}_copy`};
              setEditingTemplate(duplicated);
              setShowCreateModal(true);
            }}
            title="Duplicate"
          >
            <FaCopy />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={!permissions.delete}
            onClick={() => {
              setSelectedTemplate(row);
              setShowDeleteModal(true);
            }}
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  // Render Preview with Sample Data
  const renderPreview = (template) => {
    if (!template) return '';
    
    let content = template.body;
    const sampleData = {
      username: 'johndoe',
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      siteName: 'PayEarth',
      siteUrl: 'https://payearth.com',
      orderNumber: 'ORD-12345',
      orderDate: 'January 15, 2024',
      orderTotal: '$299.99',
      projectName: 'Website Development',
      projectId: 'PRJ-001',
      milestoneTitle: 'Homepage Design Complete',
      milestoneDescription: 'All homepage design elements have been completed and approved.',
      amount: '$500.00',
      paymentDate: 'January 20, 2024',
      invoiceNumber: 'INV-2024-001',
      dueDate: 'February 15, 2024',
      supportUrl: 'https://payearth.com/support',
      loginUrl: 'https://payearth.com/login',
      dashboardUrl: 'https://payearth.com/dashboard'
    };

    Object.entries(sampleData).forEach(([key, value]) => {
      content = content.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });

    return content;
  };

  // Create/Edit Modal
  const CreateEditModal = () => (
    <Modal
      show={showCreateModal}
      onHide={() => {
        setShowCreateModal(false);
        setEditingTemplate(null);
      }}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelope className="me-2" />
          {editingTemplate?.id ? 'Edit Email Template' : 'Create Email Template'}
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{
          name: editingTemplate?.name || '',
          tag: editingTemplate?.tag || '',
          category: editingTemplate?.category || 'general',
          subject: editingTemplate?.subject || '',
          body: editingTemplate?.body || '',
          active: editingTemplate?.active !== undefined ? editingTemplate.active : true
        }}
        validationSchema={emailTemplateValidationSchema}
        onSubmit={handleSaveTemplate}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <Modal.Body>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Template Name *</label>
                    <Field
                      name="name"
                      type="text"
                      className="form-control"
                      placeholder="Enter template name"
                    />
                    <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Tag *</label>
                    <Field
                      name="tag"
                      type="text"
                      className="form-control"
                      placeholder="e.g., welcome_email"
                    />
                    <ErrorMessage name="tag" component="div" className="text-danger small mt-1" />
                    <small className="text-muted">Unique identifier (lowercase, underscores only)</small>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Category *</label>
                    <Field
                      name="category"
                      as="select"
                      className="form-control"
                    >
                      <option value="general">General</option>
                      <option value="user_registration">User Registration</option>
                      <option value="orders">Orders</option>
                      <option value="projects">Projects</option>
                      <option value="payments">Payments</option>
                      <option value="notifications">Notifications</option>
                    </Field>
                    <ErrorMessage name="category" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="form-check">
                      <Field
                        name="active"
                        type="checkbox"
                        className="form-check-input"
                        id="template-active"
                      />
                      <label className="form-check-label" htmlFor="template-active">
                        Active Template
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Subject Line *</label>
                <Field
                  name="subject"
                  type="text"
                  className="form-control"
                  placeholder="Enter email subject"
                />
                <ErrorMessage name="subject" component="div" className="text-danger small mt-1" />
              </div>

              <div className="mb-3 admin-cms-quill-wrap">
                <label className="form-label">Email Body *</label>
                <ReactQuill
                  value={values.body}
                  onChange={(content) => setFieldValue('body', content)}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                />
                <ErrorMessage name="body" component="div" className="text-danger small mt-1" />
              </div>

              <div className="mb-3">
                <label className="form-label">Available Variables</label>
                <div className="border p-3 rounded bg-light">
                  <div className="row">
                    {availableVariables.map((variable, index) => (
                      <div key={variable} className="col-md-3 mb-2">
                        <span
                          className="badge bg-info cursor-pointer font-monospace"
                          style={{fontSize: '11px'}}
                          onClick={() => {
                            const currentBody = values.body;
                            const newBody = currentBody + ` {{${variable}}}`;
                            setFieldValue('body', newBody);
                          }}
                          title="Click to insert"
                        >
                          {`{{${variable}}}`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted">Click on any variable to insert it into the email body</small>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTemplate(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn custom_btn btn_yellow"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" />
                    {editingTemplate?.id ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );

  // Preview Modal
  const PreviewModal = () => (
    <Modal
      show={showPreviewModal}
      onHide={() => setShowPreviewModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Email Preview: {selectedTemplate?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedTemplate && (
          <>
            <div className="mb-3">
              <label className="form-label fw-bold">Subject:</label>
              <div className="p-2 bg-light rounded">{renderPreview({...selectedTemplate, body: selectedTemplate.subject})}</div>
            </div>
            <div>
              <label className="form-label fw-bold">Email Body:</label>
              <div 
                className="p-3 bg-white rounded border"
                style={{minHeight: '300px', maxHeight: '400px', overflow: 'auto'}}
                dangerouslySetInnerHTML={{ __html: renderPreview(selectedTemplate) }}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowPreviewModal(false)}
        >
          Close
        </button>
        <button 
          type="button" 
          className="btn custom_btn btn_yellow"
          onClick={() => handleSendTestEmail(selectedTemplate)}
        >
          <FaPaperPlane className="me-1" />
          Send Test Email
        </button>
      </Modal.Footer>
    </Modal>
  );

  // Delete Modal
  const DeleteModal = () => (
    <Modal
      show={showDeleteModal}
      onHide={() => setShowDeleteModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center">
          <FaTrash className="text-danger mb-3" size={48} />
          <h5>Are you sure?</h5>
          <p className="text-muted">
            This action cannot be undone. This will permanently delete the template{' '}
            <strong>"{selectedTemplate?.name}"</strong>.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowDeleteModal(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDeleteTemplate}
        >
          <FaTrash className="me-1" />
          Delete Template
        </button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="createpost bg-white rounded-3 mt-2 addPost_left_container">
          <div className="dash_inner_wrap pb-2">
            <div className="dash_header_row">
              <div className="dash_title d-flex align-items-center">
                <FaEnvelope className="me-2" />
                Email Templates
              </div>
              <div>
                <button
                  className="btn custom_btn btn_yellow"
                  onClick={() => setShowCreateModal(true)}
                  disabled={!permissions.add}
                >
                  <FaPlus className="me-2" />
                  Add Template
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-5 text-center">
              <SpinnerLoader />
            </div>
          ) : (
            <div className="cp_body pt-0">
              <div className="admin-cms-table-wrap">
                <DataTableExtensions
                  columns={emailTemplateColumns}
                  data={emailTemplates}
                  exportHeaders={true}
                >
                  <DataTable
                    pagination
                    noHeader
                    highlightOnHover
                    defaultSortField="name"
                    defaultSortAsc={true}
                    selectedRows={selectedTemplates}
                    onSelectedRowsChange={setSelectedTemplates}
                    selectableRows
                    responsive
                  />
                </DataTableExtensions>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateEditModal />
      <PreviewModal />
      <DeleteModal />
    </div>
  );
};

// Legal Notices Management Component
const LegalNoticesManagement = ({ permissions, authInfoString }) => {
  const [legalNotices, setLegalNotices] = useState([]);
  const [selectedNotices, setSelectedNotices] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [loading, setLoading] = useState(false);

  // Validation Schema
  const legalNoticeValidationSchema = Yup.object({
    title: Yup.string()
      .required('Notice title is required')
      .min(3, 'Title must be at least 3 characters')
      .max(200, 'Title cannot exceed 200 characters'),
    content: Yup.string()
      .required('Notice content is required')
      .min(10, 'Content must be at least 10 characters'),
    location: Yup.string()
      .required('Location is required'),
    type: Yup.string()
      .required('Notice type is required'),
    priority: Yup.number()
      .required('Priority is required')
      .min(1, 'Priority must be at least 1')
      .max(10, 'Priority cannot exceed 10')
  });

  // Sample data
  const sampleLegalNotices = [
    {
      id: 1,
      title: 'Cookie Notice',
      content: 'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
      location: 'header',
      type: 'info',
      priority: 1,
      active: true,
      created_at: '2024-01-15',
      expires_at: '2024-12-31'
    },
    {
      id: 2,
      title: 'Terms of Service Update',
      content: 'Our Terms of Service have been updated to reflect new features and compliance requirements. Please review the changes before continuing to use our service.',
      location: 'dashboard',
      type: 'warning',
      priority: 2,
      active: true,
      created_at: '2024-01-10',
      expires_at: '2024-06-30'
    },
    {
      id: 3,
      title: 'Data Processing Notice',
      content: 'In accordance with GDPR regulations, we want to inform you about how we process your personal data. View our updated Privacy Policy for more details.',
      location: 'footer',
      type: 'info',
      priority: 3,
      active: false,
      created_at: '2024-01-05',
      expires_at: null
    },
    {
      id: 4,
      title: 'System Maintenance Alert',
      content: 'Scheduled maintenance will occur on Sunday, February 25th from 2:00 AM to 6:00 AM EST. Some services may be temporarily unavailable.',
      location: 'header',
      type: 'warning',
      priority: 1,
      active: true,
      created_at: '2024-02-20',
      expires_at: '2024-02-26'
    }
  ];

  // Load Legal Notices
  const loadLegalNotices = async () => {
    setLoading(true);
    try {
      const res = await axios.get('admin/legal-notices', {
        headers: {
          Authorization: `Bearer ${authInfoString.token}`,
        },
      });
      setLegalNotices(res.data.data || sampleLegalNotices);
    } catch (error) {
      console.error("Error loading legal notices: ", error);
      setLegalNotices(sampleLegalNotices);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLegalNotices();
  }, []);

  // Create/Update Legal Notice
    const handleSaveNotice = async (values, { setSubmitting, resetForm }) => {
        try {
            // Add createdBy/updatedBy fields
            const dataToSend = {
            ...values,
            createdBy: editingNotice ? undefined : authInfoString.id,
            updatedBy: editingNotice ? authInfoString.id : undefined
            };

            const endpoint = editingNotice 
            ? `admin/legal-notices/update/${editingNotice.id}`
            : 'admin/legal-notices/create';
            
            const method = editingNotice ? 'put' : 'post';
            
            const res = await axios[method](endpoint, dataToSend, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            if (res.data.status) {
            toast.success(`Notice ${editingNotice ? 'updated' : 'created'} successfully!`);
            setShowCreateModal(false);
            setEditingNotice(null);
            resetForm();
            loadLegalNotices();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error saving notice');
        } finally {
            setSubmitting(false);
        }
    };

  // Delete Legal Notice
    const handleDeleteNotice = async () => {
        if (!selectedNotice) return;

        try {
            await axios.delete(`admin/legal-notices/delete/${selectedNotice.id}`, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            toast.success('Legal notice deleted successfully!');
            setShowDeleteModal(false);
            setSelectedNotice(null);
            loadLegalNotices();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error deleting notice');
        }
    };

  // Toggle Notice Status
    const handleToggleStatus = async (notice) => {
        try {
            await axios.patch(`admin/legal-notices/toggle/${notice.id}`, {
            updatedBy: authInfoString.id
            }, {
            headers: {
                Authorization: `Bearer ${authInfoString.token}`,
            },
            });

            toast.success('Notice status updated!');
            loadLegalNotices();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating status');
        }
    };

  // Legal Notice Columns
  const legalNoticeColumns = [
    {
      name: 'TITLE',
      selector: (row) => row.title,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.title}</div>
          <small className="text-muted">Priority: {row.priority}</small>
        </div>
      ),
    },
    {
      name: 'CONTENT',
      selector: (row) => row.content,
      cell: (row) => (
        <div className="text-truncate" style={{maxWidth: '300px'}} title={row.content}>
          {row.content}
        </div>
      ),
    },
    {
      name: 'TYPE',
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => (
        <span className={`badge ${
          row.type === 'info' ? 'bg-info' :
          row.type === 'warning' ? 'bg-warning text-dark' :
          row.type === 'error' ? 'bg-danger' :
          'bg-secondary'
        }`}>
          {row.type.toUpperCase()}
        </span>
      ),
    },
    {
      name: 'LOCATION',
      selector: (row) => row.location,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-secondary">
          {row.location.charAt(0).toUpperCase() + row.location.slice(1)}
        </span>
      ),
    },
    {
      name: 'STATUS',
      selector: (row) => row.active,
      sortable: true,
      cell: (row) => (
        <button
          onClick={() => handleToggleStatus(row)}
          className="btn btn-sm p-0 border-0 d-flex align-items-center"
          disabled={!permissions.edit}
        >
          {row.active ? (
            <FaToggleOn className="text-success me-1" size={18} />
          ) : (
            <FaToggleOff className="text-muted me-1" size={18} />
          )}
          <span className={row.active ? 'text-success' : 'text-muted'}>
            {row.active ? 'Active' : 'Inactive'}
          </span>
        </button>
      ),
    },
    {
      name: 'EXPIRES',
      selector: (row) => row.expires_at,
      sortable: true,
      cell: (row) => (
        <div>
          {row.expires_at ? (
            <small className={`text-${new Date(row.expires_at) < new Date() ? 'danger' : 'muted'}`}>
              {new Date(row.expires_at).toLocaleDateString()}
            </small>
          ) : (
            <small className="text-muted">No expiry</small>
          )}
        </div>
      ),
    },
    {
      name: 'ACTIONS',
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            disabled={!permissions.edit}
            onClick={() => {
              setEditingNotice(row);
              setShowCreateModal(true);
            }}
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={!permissions.delete}
            onClick={() => {
              setSelectedNotice(row);
              setShowDeleteModal(true);
            }}
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  // Create/Edit Modal
  const CreateEditNoticeModal = () => (
    <Modal
      show={showCreateModal}
      onHide={() => {
        setShowCreateModal(false);
        setEditingNotice(null);
      }}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaGavel className="me-2" />
          {editingNotice?.id ? 'Edit Legal Notice' : 'Create Legal Notice'}
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{
          title: editingNotice?.title || '',
          content: editingNotice?.content || '',
          location: editingNotice?.location || 'header',
          type: editingNotice?.type || 'info',
          priority: editingNotice?.priority || 1,
          active: editingNotice?.active !== undefined ? editingNotice.active : true,
          expires_at: editingNotice?.expires_at || ''
        }}
        validationSchema={legalNoticeValidationSchema}
        onSubmit={handleSaveNotice}
        enableReinitialize={true}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <Modal.Body>
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Notice Title *</label>
                    <Field
                      name="title"
                      type="text"
                      className="form-control"
                      placeholder="Enter notice title"
                    />
                    <ErrorMessage name="title" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Priority *</label>
                    <Field
                      name="priority"
                      as="select"
                      className="form-control"
                    >
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <option key={num} value={num}>Priority {num}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="priority" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Notice Content *</label>
                <Field
                  name="content"
                  as="textarea"
                  rows={4}
                  className="form-control"
                  placeholder="Enter notice content"
                />
                <ErrorMessage name="content" component="div" className="text-danger small mt-1" />
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Display Location *</label>
                    <Field
                      name="location"
                      as="select"
                      className="form-control"
                    >
                      <option value="header">Header</option>
                      <option value="footer">Footer</option>
                      <option value="dashboard">Dashboard</option>
                      <option value="sidebar">Sidebar</option>
                      <option value="modal">Modal Popup</option>
                    </Field>
                    <ErrorMessage name="location" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Notice Type *</label>
                    <Field
                      name="type"
                      as="select"
                      className="form-control"
                    >
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                      <option value="success">Success</option>
                    </Field>
                    <ErrorMessage name="type" component="div" className="text-danger small mt-1" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Expires At</label>
                    <Field
                      name="expires_at"
                      type="date"
                      className="form-control"
                    />
                    <small className="text-muted">Leave empty for no expiry</small>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <Field
                    name="active"
                    type="checkbox"
                    className="form-check-input"
                    id="notice-active"
                  />
                  <label className="form-check-label" htmlFor="notice-active">
                    Active Notice
                  </label>
                </div>
              </div>

              {values.content && (
                <div className="mb-3">
                  <label className="form-label">Preview</label>
                  <div className={`alert alert-${values.type === 'error' ? 'danger' : values.type} mb-0`} role="alert">
                    <strong>{values.title}</strong>
                    <br />
                    {values.content}
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingNotice(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn custom_btn btn_yellow"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" />
                    {editingNotice?.id ? 'Update Notice' : 'Create Notice'}
                  </>
                )}
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );

  // Delete Modal
  const DeleteNoticeModal = () => (
    <Modal
      show={showDeleteModal}
      onHide={() => setShowDeleteModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center">
          <FaTrash className="text-danger mb-3" size={48} />
          <h5>Are you sure?</h5>
          <p className="text-muted">
            This action cannot be undone. This will permanently delete the notice{' '}
            <strong>"{selectedNotice?.title}"</strong>.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowDeleteModal(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDeleteNotice}
        >
          <FaTrash className="me-1" />
          Delete Notice
        </button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="createpost bg-white rounded-3 mt-2 addPost_left_container">
          <div className="dash_inner_wrap pb-2">
            <div className="dash_header_row">
              <div className="dash_title d-flex align-items-center">
                <FaGavel className="me-2" />
                Legal Notices
              </div>
              <div>
                <button
                  className="btn custom_btn btn_yellow"
                  onClick={() => setShowCreateModal(true)}
                  disabled={!permissions.add}
                >
                  <FaPlus className="me-2" />
                  Add Notice
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-5 text-center">
              <SpinnerLoader />
            </div>
          ) : (
            <div className="cp_body pt-0">
              <div className="admin-cms-table-wrap">
                <DataTableExtensions
                  columns={legalNoticeColumns}
                  data={legalNotices}
                  exportHeaders={true}
                >
                  <DataTable
                    pagination
                    noHeader
                    highlightOnHover
                    defaultSortField="priority"
                    defaultSortAsc={true}
                    selectedRows={selectedNotices}
                    onSelectedRowsChange={setSelectedNotices}
                    selectableRows
                    responsive
                  />
                </DataTableExtensions>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateEditNoticeModal />
      <DeleteNoticeModal />
    </div>
  );
};

// Usage Example Component
const CMSComponents = () => {
  const authInfoString = JSON.parse(localStorage.getItem("authInfo"));
  const [permissions, setPermissions] = useState({
    add: true,
    edit: true,
    delete: true
  });

  return (
    <div>
      {/* Custom Styles */}
      <style jsx>{`
        .custom_btn {
          border-radius: 6px;
          font-weight: 500;
          padding: 8px 16px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .btn_yellow {
          background-color: #ffc107;
          border-color: #ffc107;
          color: #000;
        }

        .btn_yellow:hover {
          background-color: #e0a800;
          border-color: #d39e00;
          color: #000;
        }

        .createpost {
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          border: 1px solid #e9ecef;
        }

        .addPost_left_container {
          overflow: hidden;
        }

        .dash_inner_wrap {
          padding: 15px 20px;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }

        .dash_title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .text-truncate {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .font-monospace {
          font-family: 'Courier New', Courier, monospace;
        }

        .container {
          max-width: 1200px;
        }

        .ql-editor {
          min-height: 150px;
        }
      `}</style>

      {/* Email Template Management */}
      <EmailTemplateManagement 
        permissions={permissions}
        authInfoString={authInfoString}
      />

      {/* Legal Notices Management */}
      <LegalNoticesManagement 
        permissions={permissions}
        authInfoString={authInfoString}
      />
    </div>
  );
};

export { EmailTemplateManagement, LegalNoticesManagement };
export default CMSComponents;